import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	AnnotationProps,
	AnnotationState,
	AnnotationToolType,
	CropAreaType,
} from './Annotations.types';
import { scrollToType } from '../../Pages/Flo/Flo.types';
import styles from './Annotations.module.css';
import classNames from 'classnames';
import { max, min, noop } from 'lodash';
import { Circle, Layer, Line, Rect, Stage } from 'react-konva';
import { cloneDeep, find, get, isFunction, map, memoize, sortBy } from 'lodash';
import uniqId from 'uniqid';
import Konva from 'konva';
import KonvaEventObject = Konva.KonvaEventObject;
import { scaleLinear } from 'd3-scale';
import { useSelector } from 'react-redux';
import { ReactComponent as PointerSvg } from './../../Common/images/annotation-pointer.svg';
import { calculateAspectRatioFit } from '../../utils/video.utils';
import { MIN_CROP_HEIGHT, MIN_CROP_WIDTH } from '../../Common/Common.params';
import { IconMessageCircle2Filled as CommentIcon } from '@tabler/icons-react';
import { getWindowDimensions } from '../../Common/Common.utils';

const defaultStrokeWidth = 2;
const strokeWidthWhenActive = 4;

const CROP_MASK_COLOR = '#00000066';

const Annotation = ({
	strokeWidth = defaultStrokeWidth,
	height,
	width,
	element,
	scrollTo,
}: {
	strokeWidth?: number;
	noHover?: boolean;
	element: AnnotationState;
	offsetHeight: number;
	height: number;
	width: number;
	offsetWidth: number;
	scrollTo?: scrollToType;
}) => {
	const { highlightCommentId, highlightAnnotationByCommentId } = scrollTo || {};
	const [active, setActive] = useState(element.commentId === highlightCommentId);

	useEffect(() => {
		setActive(element.commentId === highlightCommentId);
	}, [highlightCommentId, element?.commentId]);

	const fgColor =
		get(element, 'createdBy.userProfile.profilePreference.avatarColor.bgColor') ||
		'#137866';
	const yScale = scaleLinear(
		[0, get(element, 'windowSize.height', 0)],
		[0, height - strokeWidth * 2]
	);
	const xScale = scaleLinear([0, get(element, 'windowSize.width', 0)], [0, width]);

	if (highlightAnnotationByCommentId && !active && element?.commentId) {
		return <Rect x={0} y={0} width={0} height={0} />;
	}
	if (element.tool.includes('rect')) {
		return (
			<Rect
				key={`rect_${element.key}`}
				x={xScale(element?.x || 0)}
				y={yScale(element?.y || 0)}
				width={xScale(element?.width || 0)}
				height={yScale(element?.height || 0)}
				stroke={fgColor}
				strokeWidth={active ? strokeWidthWhenActive : strokeWidth}
			/>
		);
	}
	return (
		<Line
			key={`line_${element.key}`}
			autoDrawEnabled
			enableTrace
			points={element?.points}
			stroke="red"
			strokeWidth={element.tool === 'eraser' ? 20 : 5}
			tension={0.1}
			lineJoin="round"
			lineCap="round"
			globalCompositeOperation={
				element.tool === 'eraser' ? 'destination-out' : 'source-over'
			}
		/>
	);
};

const AreaSelector = ({
	x,
	y,
	x2,
	y2,
	windowSize,
	currentViewPortHeight,
	currentViewPortWidth,
	videoSize,
	setAreaSelected = noop,
}: CropAreaType & {
	videoSize: {
		width: number;
		height: number;
	};
	currentViewPortHeight: number;
	currentViewPortWidth: number;
	setAreaSelected(area: CropAreaType): void;
}) => {
	const [clipPosition, setClipPosition] = useState('');
	const [isMouseDown, setMouseDown] = useState(false);
	const [startPosition, setStartPosition] = useState({
		x: 0,
		y: 0,
	});
	const yScale = scaleLinear(
		[0, get(windowSize, 'height', 0)],
		[0, currentViewPortHeight]
	);
	const xScale = scaleLinear([0, get(windowSize, 'width', 0)], [0, currentViewPortWidth]);

	const xScaleActualVideo = scaleLinear([0, videoSize.width], [0, currentViewPortHeight]);
	const yScaleActualVideo = scaleLinear([0, videoSize.width], [0, currentViewPortHeight]);

	const scaledX = xScale(x || 0);
	const scaledY = yScale(y || 0);

	const scaledX2 = xScale(x2 || 0);
	const scaledY2 = yScale(y2 || 0);
	const scaledWidth = Math.abs(scaledX - scaledX2);
	const scaledHeight = Math.abs(scaledY - scaledY2);

	const onMouseUp = useCallback(() => {
		setClipPosition('');
	}, [setClipPosition]);

	const onMouseMove = useCallback(
		(event: KonvaEventObject<MouseEvent>) => {
			const stage = event.target.getStage();
			if (!isMouseDown) {
				// @ts-ignore
				stage.container().style.cursor = 'pointer';
				return;
			}
			// @ts-ignore
			stage.container().style.cursor = 'move';

			// @ts-ignore
			const point = stage.getPointerPosition() || { x: 0, y: 0 };
			let pointX = point.x || 0;
			let pointY = point.y || 0;
			const minCropWidth = xScaleActualVideo(MIN_CROP_WIDTH);
			const minCropHeight = yScaleActualVideo(MIN_CROP_HEIGHT);
			switch (clipPosition) {
				case 'rect': {
					const diffX = startPosition.x - pointX;
					const diffY = startPosition.y - pointY;
					const width = scaledX2 - scaledX;
					const height = scaledY2 - scaledY;

					let actualX = max([scaledX - diffX, 0]) as number;
					let actualY = max([scaledY - diffY, 0]) as number;

					actualX = min([currentViewPortWidth - width, actualX]) as number;
					actualY = min([currentViewPortHeight - height, actualY]) as number;

					setAreaSelected({
						x2: actualX + width,
						y2: actualY + height,
						x: actualX,
						y: actualY,
						windowSize: {
							width: currentViewPortWidth,
							height: currentViewPortHeight,
						},
					});
					setStartPosition({
						x: pointX,
						y: pointY,
					});
					break;
				}
				case 'top-left': {
					if (scaledX2 - pointX < minCropWidth) {
						pointX = scaledX;
					}

					if (scaledY2 - pointY < minCropHeight) {
						pointY = scaledY;
					}

					const { width: newWidth, height: newHeight } = calculateAspectRatioFit(
						16,
						9,
						Math.abs(scaledX2 - pointX),
						Math.abs(scaledY2 - pointY)
					);

					setAreaSelected({
						x2: scaledX2,
						y2: scaledY2,
						x: scaledX2 - newWidth,
						y: scaledY2 - newHeight,
						windowSize: {
							width: currentViewPortWidth,
							height: currentViewPortHeight,
						},
					});
					break;
				}
				case 'top-right': {
					if (pointX - scaledX < minCropWidth) {
						pointX = scaledX2;
					}

					if (scaledY2 - pointY < minCropHeight) {
						pointY = scaledY;
					}
					const { width: newWidth, height: newHeight } = calculateAspectRatioFit(
						16,
						9,
						Math.abs(scaledX - pointX),
						Math.abs(scaledY2 - pointY)
					);
					setAreaSelected({
						x2: scaledX + newWidth,
						y2: scaledY2,
						x: scaledX,
						y: scaledY2 - newHeight,
						windowSize: {
							width: currentViewPortWidth,
							height: currentViewPortHeight,
						},
					});
					break;
				}
				case 'bottom-left': {
					if (scaledX2 - pointX < minCropWidth) {
						pointX = scaledX;
					}

					if (pointY - scaledY < minCropHeight) {
						pointY = scaledY2;
					}

					const { width: newWidth, height: newHeight } = calculateAspectRatioFit(
						16,
						9,
						Math.abs(scaledX2 - pointX),
						Math.abs(scaledY - pointY)
					);
					setAreaSelected({
						x2: scaledX2,
						y2: scaledY + newHeight,
						x: scaledX2 - newWidth,
						y: scaledY,
						windowSize: {
							width: currentViewPortWidth,
							height: currentViewPortHeight,
						},
					});
					break;
				}
				case 'bottom-right': {
					if (pointX - scaledX < minCropWidth) {
						pointX = scaledX2;
					}

					if (pointY - scaledY < minCropHeight) {
						pointY = scaledY2;
					}

					const { width: newWidth, height: newHeight } = calculateAspectRatioFit(
						16,
						9,
						Math.abs(scaledX - pointX),
						Math.abs(scaledY - pointY)
					);

					setAreaSelected({
						x2: min([scaledX + newWidth, currentViewPortWidth]) as number,
						y2: min([scaledY + newHeight, currentViewPortHeight]) as number,
						x: scaledX,
						y: scaledY,
						windowSize: {
							width: currentViewPortWidth,
							height: currentViewPortHeight,
						},
					});
					break;
				}
			}
		},
		[
			isMouseDown,
			startPosition,
			setStartPosition,
			clipPosition,
			x,
			y,
			x2,
			y2,
			currentViewPortHeight,
			currentViewPortWidth,
			setAreaSelected,
		]
	);

	const onMouseDown = useCallback(
		(event: KonvaEventObject<MouseEvent>) => {
			const stage = event.target.getStage();
			// @ts-ignore
			const point = stage.getPointerPosition() || { x: 0, y: 0 };
			const diffTopX = Math.abs(scaledX - point.x);
			const diffTopY = Math.abs(scaledY - point.y);
			const diffBottomX = Math.abs(scaledX2 - point.x);
			const diffBottomY = Math.abs(scaledY2 - point.y);
			let position = '';
			if (diffTopX < 10) {
				if (diffTopY < 10) {
					position = 'top-left';
				}
				if (diffBottomY < 10) {
					position = 'bottom-left';
				}
			}

			if (diffBottomX < 10) {
				if (diffTopY < 10) {
					position = 'top-right';
				}
				if (diffBottomY < 10) {
					position = 'bottom-right';
				}
			}
			if (
				!position &&
				point.x > scaledX &&
				point.y > scaledY &&
				point.x < scaledX2 &&
				point.y < scaledY2
			) {
				position = 'rect';
			}
			setClipPosition(position);
			setStartPosition({
				x: point.x,
				y: point.y,
			});
		},
		[setClipPosition, setStartPosition, scaledX, scaledX2, scaledY, scaledY2]
	);

	useEffect(() => {
		const onMouseUpGlobal = () => {
			setMouseDown(false);
			if (clipPosition) {
				onMouseUp();
			}
		};
		const onMouseDownGlobal = () => {
			setMouseDown(true);
		};
		window.addEventListener('mousedown', onMouseDownGlobal, true);
		window.addEventListener('mouseup', onMouseUpGlobal, true);
		return () => {
			window.removeEventListener('mousedown', onMouseDownGlobal, true);
			window.addEventListener('mouseup', onMouseUpGlobal, true);
		};
	}, [setMouseDown, onMouseUp, clipPosition]);

	return (
		<Stage
			listening
			className={classNames(styles['floik-canvas'])}
			width={currentViewPortWidth}
			height={currentViewPortHeight}
			onMouseUp={onMouseUp}
			onMouseMove={onMouseMove}
			onMouseDown={onMouseDown}
		>
			<Layer>
				<Rect
					x={0}
					y={0}
					width={currentViewPortWidth}
					height={scaledY + 0.1}
					fill={CROP_MASK_COLOR}
					filters={[Konva.Filters.Mask, Konva.Filters.Noise]}
					shadowBlur={1}
					strokeWidth={0}
				/>
				<Rect
					x={0}
					y={scaledY2 - 0.1}
					width={currentViewPortWidth}
					height={currentViewPortHeight - scaledY2}
					fill={CROP_MASK_COLOR}
					filters={[Konva.Filters.Mask, Konva.Filters.Noise]}
					shadowBlur={1}
					strokeWidth={0}
				/>
				<Rect
					x={0}
					y={scaledY}
					width={scaledX}
					height={scaledHeight}
					fill={CROP_MASK_COLOR}
					filters={[Konva.Filters.Mask, Konva.Filters.Noise]}
					shadowBlur={1}
					strokeWidth={0}
				/>
				<Rect
					x={scaledX2}
					y={scaledY}
					width={currentViewPortWidth - scaledX2}
					height={scaledHeight}
					fill={CROP_MASK_COLOR}
					filters={[Konva.Filters.Mask, Konva.Filters.Noise]}
					shadowBlur={1}
					strokeWidth={0}
				/>
				<Rect
					name="rect"
					dashEnabled
					dash={[6]}
					x={scaledX}
					y={scaledY}
					width={scaledWidth}
					height={scaledHeight}
					stroke="white"
					strokeWidth={2}
					opacity={0.6}
				/>
				<Circle
					name="top-left"
					x={scaledX + 2}
					y={scaledY + 2}
					radius={5}
					stroke="white"
					fill="black"
				/>
				<Circle
					name="top-right"
					x={scaledX + scaledWidth - 2}
					y={scaledY + 2}
					radius={5}
					stroke="white"
					fill="black"
				/>
				<Circle
					name="bottom-left"
					x={scaledX + 2}
					y={scaledY + scaledHeight - 2}
					radius={5}
					stroke="white"
					fill="black"
				/>
				<Circle
					name="bottom-right"
					x={scaledX + scaledWidth - 2}
					y={scaledY + scaledHeight - 2}
					radius={5}
					stroke="white"
					fill="black"
				/>
			</Layer>
		</Stage>
	);
};

const Annotations = ({
	className,
	isImage,
	offsetWidth = 0,
	allowComment,
	doNotSetTop,
	offsetHeight = 0,
	isFloClosed,
	width: vWidth,
	height: vHeight,
	actionType,
	enable,
	showData,
	continuePlay,
	onDone,
	player,
	elements,
	tool = AnnotationToolType.rect,
	onAnnotationClick,
	addingComment,
	currentUser,
	onAnnotationProgress,
	videoSize = {
		width: 0,
		height: 0,
	},
	areaSelected = {
		x: 0,
		y: 0,
		x2: MIN_CROP_WIDTH,
		y2: MIN_CROP_HEIGHT,
		windowSize: {
			width: MIN_CROP_WIDTH,
			height: MIN_CROP_HEIGHT,
		},
	},
	onAreaSelected,
	floId,
	floIndex,
}: Partial<AnnotationProps>) => {
	const pointerSvg = useRef();
	const [activeElement, setActiveElement] = useState('');
	const [staging, setStaging] = useState<AnnotationState | null>(null);

	const windowDimensions = getWindowDimensions(floIndex);
	const width = vWidth || windowDimensions.width;
	const height = vHeight || windowDimensions.height;

	const onAreaSelectedCallback = useCallback(
		(cropData: CropAreaType) => {
			if (isFunction(onAreaSelected)) {
				onAreaSelected(cropData);
			}
		},
		[onAreaSelected]
	);

	const onComplete = (event: KonvaEventObject<MouseEvent>) => {
		if (allowComment) {
			return;
		}
		if (isFunction(onAnnotationProgress)) {
			onAnnotationProgress(false);
		}
		// @ts-ignore
		if (pointerSvg.current && pointerSvg.current?.style) {
			// @ts-ignore
			pointerSvg.current.style.visibility = 'hidden';
		}
		if (!staging) {
			if (activeElement) {
				if (onAnnotationClick) onAnnotationClick(activeElement);
			}
			return;
		}
		if (onDone) {
			if (
				Math.abs(staging.width || 0) * Math.abs(staging.height || 0) > 10 &&
				!isFloClosed
			) {
				onDone(
					{
						// @ts-ignore
						time: player?.currentTime,
						uid: uniqId(),
						// @ts-ignore
						state: [{ ...staging, time: player?.currentTime }],
					},
					event
				);
			} else if (activeElement && onAnnotationClick) {
				onAnnotationClick(activeElement);
			} else {
				if (continuePlay) {
					continuePlay();
				}
			}
		}
		setStaging(null);
	};
	const scrollTo = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.scrollTo`)
	);
	if (!enable) return <span></span>;
	const newVideoSize = calculateAspectRatioFit(
		videoSize.width || 16,
		videoSize.height || 9,
		width,
		height
	);

	const offsetLeft = (width - newVideoSize.width) / 2;
	const offsetTop = (height - newVideoSize.height) / 2;
	return (
		<div
			className={classNames(className, styles['floik-annotation-container'], {
				[styles['canvas-enabled']]: !allowComment && enable && !addingComment,
				[styles['cropping']]: actionType === 'crop',
				[styles['allowComment']]: allowComment,
			})}
			style={{
				// left: offsetWidth + offsetLeft,
				top: doNotSetTop ? undefined : offsetTop || 0,
				width: newVideoSize.width,
				height: newVideoSize.height,
			}}
		>
			<PointerSvg ref={pointerSvg} className={styles.annotationPointer} />
			{map(elements, (elem: AnnotationState, index: number) => {
				const yScale = scaleLinear(
					[0, get(elem, 'windowSize.height', 0)],
					[0, height - defaultStrokeWidth * 2]
				);
				const xScale = scaleLinear([0, get(elem, 'windowSize.width', 0)], [0, width]);

				return (
					!!elem && (
						<div
							key={`${elem.commentId}_${elem.id}`}
							style={{
								top: yScale(elem?.y || 0),
								left: xScale(elem?.x || 0),
								width: 30,
								height: 30,
								position: 'absolute',
							}}
						>
							<CommentIcon className={styles.commentIcon} />
						</div>
					)
				);
			})}

			{actionType !== 'crop' ? (
				<Stage
					className={classNames(styles['floik-canvas'])}
					width={newVideoSize.width}
					height={newVideoSize.height}
					onMouseLeave={onComplete}
					onMouseUp={onComplete}
					onMouseMove={(event: KonvaEventObject<MouseEvent>) => {
						if (allowComment) return;
						// @ts-ignore
						if (pointerSvg.current && pointerSvg.current?.style) {
							// @ts-ignore
							pointerSvg.current.style.visibility = 'visible';
							if (!isImage) {
								// @ts-ignore
								pointerSvg.current.style.left = event.evt.offsetX - 10;
								// @ts-ignore
								pointerSvg.current.style.top = event.evt.offsetY - 10;
							} else {
								const stage = event.target.getStage();
								// @ts-ignore
								const point = stage.getPointerPosition() || { x: 0, y: 0 };
								// @ts-ignore
								pointerSvg.current.style.left = point.x - 10;
								// @ts-ignore
								pointerSvg.current.style.top = point.y - 10;
							}
						}
					}}
					onMouseDown={(event: KonvaEventObject<MouseEvent>) => {
						if (allowComment) {
							if (continuePlay) {
								continuePlay();
							}
							return;
						}
						if (isFunction(onAnnotationProgress)) {
							onAnnotationProgress(true);
						}
						const Stage = event.target.getStage();
						// @ts-ignore
						const point = Stage.getPointerPosition();
						const stage = {
							key: uniqId(),
							tool,
							time: player?.currentTime,
							windowSize: {
								height,
								width,
								offsetHeight,
								offsetWidth,
							},
							x: point?.x || 0,
							y: point?.y || 0,
							width: 32,
							height: 32,
							points: [],
						};
						if (!addingComment) {
							setStaging(stage as AnnotationState);
						}
					}}
				>
					<Layer>
						{map(
							elements,
							(elem: AnnotationState, index: number) =>
								!!elem && (
									<Annotation
										key={elem.key || elem.id || get(elem, 'uid')}
										element={elem}
										height={height}
										width={width}
										offsetHeight={offsetHeight}
										offsetWidth={offsetWidth}
										strokeWidth={
											activeElement === elem.commentId ||
											activeElement === elem.id ||
											activeElement === elem.key
												? 4
												: 2
										}
										scrollTo={scrollTo}
									/>
								)
						)}
						{!isFloClosed && staging && (
							<Annotation
								height={height}
								width={width}
								offsetHeight={offsetHeight}
								offsetWidth={offsetWidth}
								element={staging}
								strokeWidth={
									activeElement === staging.key || activeElement === staging.id ? 4 : 2
								}
								scrollTo={scrollTo}
							/>
						)}
					</Layer>
				</Stage>
			) : (
				<AreaSelector
					{...areaSelected}
					videoSize={videoSize}
					setAreaSelected={onAreaSelectedCallback}
					currentViewPortHeight={newVideoSize.height}
					currentViewPortWidth={newVideoSize.width - offsetWidth}
				/>
			)}
		</div>
	);
};

export default Annotations;
